import * as d3 from 'd3';
import { primaryColor } from 'styles/colors';
//TODO look over this. A lot of it will not be unnecessary (or at least it won't be once all apps and widgets are updated)
// Do need to grab scale definitions
const COLOR_SCALE_OPTIONS = {
	AUTO: 'Auto',
	SPECTRAL: 'Spectral',
	TURBO: 'Turbo',
	CATEGORICAL: 'Categorical',
	CLASSIFICATION: 'Classification',
	SEVERITY: 'Severity',
	PRIMARY: 'Primary',
	BOOLEAN: 'Boolean',
	NONE: 'None',
};
const CATEGORICAL_LIMIT = 10;

// Severity helpers
const lowRegex = /\bl|low|lo\b/gi;
const midRegex = /\bm|med|medium\b/gi;
const highRegex = /\bh|hi|high\b/gi;
const criticalRegex = /\bc|crit|critical\b/gi;

const severityScale = d3.interpolateYlOrRd;
const SEVERITY_COLORS = {
	[lowRegex]: severityScale(0.25),
	[midRegex]: severityScale(0.5),
	[highRegex]: severityScale(0.75),
	[criticalRegex]: severityScale(1),
};

const regexes = [lowRegex, midRegex, highRegex, criticalRegex];

// Determines if the given values can be colored with a severity color scale
export const isSeverityScale = (values) => {
	const matches = values.map((value) => {
		return regexes.some((regex) => {
			const match = value.match(regex);
			return match ? match.length > 0 : false;
		});
	});
	const matchCount = matches.reduce((a, b) => {
		return a + b;
	}, 0);
	return matchCount >= 3;
};

// Classification Helpers
const unclassRegex = /\bun.?class(ified)?\b/gi;
const secretRegex = /\bsecret\b/gi;
const topSecretRegex = /\btop.?secret\b/gi;
const CLASSIFICATION_COLORS = {
	[unclassRegex]: '#00a728',
	[secretRegex]: '#f23521',
	[topSecretRegex]: '#fd8d10',
};
const classificationRegexes = [unclassRegex, secretRegex, topSecretRegex];

// Determines if the given values can be colored with a classification color scale
export const isClassificationScale = (values) => {
	const matches = values.map((value) => {
		return classificationRegexes.some((regex) => {
			const match = value.match(regex);
			return match ? match.length > 0 : false;
		});
	});
	const matchCount = matches.reduce((a, b) => {
		return a + b;
	}, 0);
	return matchCount >= 3;
};

// Boolean helpers
const trueRegex = /\bt|true|1\b/gi;
const falseRegex = /\bf|false|0\b/gi;
const booleanRegexes = [trueRegex, falseRegex];

// Determines if the given values can be colored with a boolean color scale
export const isBooleanScale = (values) => {
	const matches = values.map((value) => {
		return booleanRegexes.some((regex) => {
			const match = value.match(regex);
			return match ? match.length > 0 : false;
		});
	});
	const matchCount = matches.reduce((a, b) => {
		return a + b;
	}, 0);
	return matchCount === 2 && values.length === 2;
};

export const SERBERUS_COLORS = [
	'#00AFAD',
	'#FBB034',
	'#3D387A',
	'#C5168D',
	'#33647A',
	'#F7931D',
	'#3589AA',
	'#940064',
	'#CB7317',
	'#B3D234',
	'#00A76D',
	'#CA5868',
	'#65C4BE',
	'#585FAA',
	'#B34B14',
	'#688766',
	'#504971',
	'#950F43',
	'#754098',
	'#3A96AA',
	'#615489',
	'#7AB3BE',
	'#61335D',
	'#B33F97',
	'#396353',
	'#45812B',
	'#8C3754',
	'#EF4156',
	'#005BAB',
	'#F15A22',
	'#934D11',
	'#799A28',
	'#ED0677',
	'#E07E27',
	'#44C7F4',
	'#A22D3F',
	'#CD822A',
	'#815017',
	'#2A457A',
	'#D4B004',
	'#009EDB',
	'#0DB14B',
	'#AA8B15',
	'#0088B2',
	'#5B8292',
	'#882972',
	'#00AE9C',
];

// Returns a color scale based on values and the passed in scale type
export function getColorScale(colorScaleType, values) {
	const valueLength = values.length;
	switch (colorScaleType) {
		default:
		case COLOR_SCALE_OPTIONS.AUTO:
			if (valueLength > SERBERUS_COLORS.length) {
				return getColorScale(COLOR_SCALE_OPTIONS.SPECTRAL, values);
			} else {
				if (isSeverityScale(values)) {
					return getColorScale(COLOR_SCALE_OPTIONS.SEVERITY, values);
				} else if (isClassificationScale(values)) {
					return getColorScale(COLOR_SCALE_OPTIONS.CLASSIFICATION, values);
				} else if (isBooleanScale(values)) {
					return getColorScale(COLOR_SCALE_OPTIONS.BOOLEAN, values);
				} else {
					return getColorScale(COLOR_SCALE_OPTIONS.CATEGORICAL, values);
				}
			}
		case COLOR_SCALE_OPTIONS.SEVERITY: {
			const colors = values.reduce((coll, value) => {
				const regex = regexes.find((regex) => {
					const matches = value.match(regex);
					return matches && matches.length > 0;
				});

				coll[value] = SEVERITY_COLORS[regex] || primaryColor;
				return coll;
			}, {});

			return (value) => colors[value];
		}
		case COLOR_SCALE_OPTIONS.CLASSIFICATION: {
			const colors = values.map((value) => {
				const regex = regexes.find((regex) => {
					const matches = value.match(regex);
					return matches && matches.length > 0;
				});

				return CLASSIFICATION_COLORS[regex] || primaryColor;
			});

			return (valueIdx) => colors[valueIdx];
		}
		case COLOR_SCALE_OPTIONS.BOOLEAN:
			// Truthy values return the primary color, falsy return a disabled grey color
			return (valueIdx) => (trueRegex.test(values[valueIdx]) ? primaryColor : '#e8e5e3');
		case COLOR_SCALE_OPTIONS.PRIMARY:
			return d3
				.scaleLinear()
				.domain([0, valueLength * 1.25])
				.range([primaryColor, '#FFFFFF']);
		case COLOR_SCALE_OPTIONS.SPECTRAL:
			return d3.scaleSequential(d3.interpolateSpectral).domain([0, valueLength]);
		case COLOR_SCALE_OPTIONS.TURBO:
			return d3.scaleSequential(d3.interpolateTurbo).domain([0, valueLength]);
		case COLOR_SCALE_OPTIONS.CATEGORICAL:
			return (valueIdx) => SERBERUS_COLORS[valueIdx % SERBERUS_COLORS.length];
		case COLOR_SCALE_OPTIONS.NONE:
			return () => primaryColor;
	}
}

export function invertColor(color, bw = true) {
	if (!color) return '#000';

	let hex = d3.color(color).formatHex();

	if (hex.indexOf('#') === 0) {
		hex = hex.slice(1);
	}

	// convert 3-digit hex to 6-digits.
	if (hex.length === 3) {
		hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
	}

	if (hex.length !== 6) {
		throw new Error('Invalid HEX color.' + hex);
	}

	let r = parseInt(hex.slice(0, 2), 16);
	let g = parseInt(hex.slice(2, 4), 16);
	let b = parseInt(hex.slice(4, 6), 16);

	if (bw) {
		// http://stackoverflow.com/a/3943023/112731
		return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#FFFFFF';
	}

	// invert color components
	r = (255 - r).toString(16);
	g = (255 - g).toString(16);
	b = (255 - b).toString(16);

	// pad each with zeros and return
	return '#' + padZero(r) + padZero(g) + padZero(b);
}

export function adjustHexOpacity(color, opacity) {
	if (isHex(color)) {
		const r = parseInt(color.slice(1, 3), 16);
		const g = parseInt(color.slice(3, 5), 16);
		const b = parseInt(color.slice(5, 7), 16);

		return `rgba(${r},${g},${b},${opacity})`;
	} else {
		console.warn(`The value ${color} is not a valid hex color`);
		return color;
	}
}

function isHex(h) {
	const hexColorRegEx = /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i;
	return hexColorRegEx.test(h);
}

function padZero(str, len) {
	len = len || 2;
	const zeros = new Array(len).join('0');
	return (zeros + str).slice(-len);
}
